import type { LensId, CountryCode } from '@/lib/types';
import { getSlugRegistry, getSlugForCountry, getCitySlugEntry, getCitySlugForId } from '@/lib/i18n';

/** Minimal state shape needed by URL sync functions. */
export interface UrlSyncState {
  activeLens: LensId;
  activeEntity: string;
  year: number;
  month: number;
}

/**
 * Parse the current pathname to extract activeLens and activeEntity.
 * Supports legacy `/countries/xx`, `/cities/xx`, and slug-based URLs.
 */
export function readPathState(pathname: string): Partial<Pick<UrlSyncState, 'activeLens' | 'activeEntity'>> {
  // Legacy country URLs
  const countryMatch = pathname.match(/^\/countries\/([^/?#]+)/i);
  if (countryMatch) {
    return { activeLens: 'country', activeEntity: countryMatch[1].toUpperCase() };
  }

  const cityMatch = pathname.match(/^\/cities\/([^/?#]+)/i);
  if (cityMatch) {
    return { activeLens: 'city', activeEntity: decodeURIComponent(cityMatch[1]) };
  }

  // New slug-based URLs: /indonesia-public-holidays-2026
  const slug = pathname.replace(/^\//, '').replace(/\/$/, '');
  if (slug) {
    try {
      const registry = getSlugRegistry();
      const match = registry[slug];
      if (match) {
        return { activeLens: 'country', activeEntity: match.code };
      }
    } catch {
      // Registry not available on client — ignore
    }

    // Try city slug: /jakarta-festivals-events-2026
    try {
      const cityEntry = getCitySlugEntry(slug);
      if (cityEntry) {
        return { activeLens: 'city', activeEntity: cityEntry.cityId };
      }
    } catch {
      // Registry not available on client — ignore
    }
  }

  return {};
}

/**
 * Build a URL path from the given activeLens and activeEntity.
 */
export function getPathForState(state: Pick<UrlSyncState, 'activeLens' | 'activeEntity'>): string {
  if (state.activeLens === 'country') {
    try {
      const slug = getSlugForCountry(state.activeEntity as CountryCode, 'en');
      if (slug) return `/${slug}`;
    } catch {
      // Fallback if registry unavailable
    }
    return `/countries/${state.activeEntity.toLowerCase()}`;
  }
  if (state.activeLens === 'city') {
    try {
      const citySlug = getCitySlugForId(state.activeEntity);
      if (citySlug) return `/${citySlug}`;
    } catch {
      // Fallback if registry unavailable
    }
    return '/';
  }
  return '/';
}

/**
 * Synchronize the given state to the browser URL via `history.replaceState`.
 */
/** Lenses that overlay the current page without changing the URL */
const OVERLAY_LENSES = new Set<string>(['long-weekend', 'nature']);

export function syncUrlParams(state: Pick<UrlSyncState, 'activeLens' | 'activeEntity' | 'year' | 'month'>) {
  if (typeof window === 'undefined') return;

  // Overlay lenses don't change the URL — they filter in place
  if (OVERLAY_LENSES.has(state.activeLens)) return;

  const params = new URLSearchParams(window.location.search);
  params.set('year', String(state.year));
  params.set('month', String(state.month));

  if (state.activeLens === 'country' || state.activeLens === 'city') {
    params.delete('lens');
    params.delete('entity');
  } else {
    params.set('lens', state.activeLens);
    params.set('entity', state.activeEntity);
  }

  const query = params.toString();
  const path = getPathForState(state);
  const nextUrl = query ? `${path}?${query}` : path;
  window.history.replaceState({}, '', nextUrl);
}

/**
 * Read initial calendar state from the current URL (pathname + search params).
 */
export function readUrlParams() {
  if (typeof window === 'undefined') return {};
  const pathState = readPathState(window.location.pathname);
  const params = new URLSearchParams(window.location.search);
  const result: Record<string, unknown> = {};

  if (pathState.activeLens) {
    result.activeLens = pathState.activeLens;
  } else {
    const lens = params.get('lens');
    if (lens && ['country', 'city', 'religion', 'nature'].includes(lens)) {
      result.activeLens = lens as LensId;
    }
  }

  if (pathState.activeEntity) {
    result.activeEntity = pathState.activeEntity;
  } else {
    const entity = params.get('entity');
    if (entity) result.activeEntity = entity;
  }

  const year = params.get('year');
  if (year) {
    const parsed = parseInt(year, 10);
    if (!isNaN(parsed) && parsed >= 2020 && parsed <= 2030) result.year = parsed;
  }
  const month = params.get('month');
  if (month !== null) {
    const parsed = parseInt(month, 10);
    if (!isNaN(parsed) && parsed >= 0 && parsed <= 11) result.month = parsed;
  }
  return result;
}
